import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
    EMPTY_PROFILE,
    DEFAULT_PREFERENCES,
    DEFAULT_NOTIFICATIONS,
} from '../types/profile';
import type {
    ActivityEntry,
    Booking,
    NotificationSettings,
    ProfileState,
    SavedTrip,
    TravelPreferences,
} from '../types/profile';

const STORAGE_KEY = 'adiona.profile.v1';
const MAX_ACTIVITY = 50;

const newId = (prefix: string) =>
    `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

/**
 * Merge stored state over defaults so a profile saved by an older build
 * still loads when new preference fields are added.
 *
 * Also mints a guest `userId` when one is missing (brand-new profile, or one
 * stored before `userId` existed) so the shape is always valid even before
 * Auth0 has resolved. `ProfileProvider`'s effect overwrites this with the
 * real Auth0 subject as soon as a login is confirmed — this is just the
 * offline-first placeholder until then.
 */
const hydrate = (raw: string | null): ProfileState => {
    if (!raw) return { ...EMPTY_PROFILE, userId: newId('usr') };
    try {
        const parsed = JSON.parse(raw) as Partial<ProfileState>;
        return {
            ...EMPTY_PROFILE,
            ...parsed,
            userId: parsed.userId || newId('usr'),
            preferences: { ...DEFAULT_PREFERENCES, ...(parsed.preferences || {}) },
            notifications: { ...DEFAULT_NOTIFICATIONS, ...(parsed.notifications || {}) },
            bookings: Array.isArray(parsed.bookings) ? parsed.bookings : [],
            savedTrips: Array.isArray(parsed.savedTrips) ? parsed.savedTrips : [],
            activity: Array.isArray(parsed.activity) ? parsed.activity : [],
        };
    } catch {
        return { ...EMPTY_PROFILE, userId: newId('usr') };
    }
};

type ProfileContextValue = {
    profile: ProfileState;
    updateProfile: (patch: Partial<Pick<ProfileState, 'displayName' | 'bio' | 'homeCity'>>) => void;
    updatePreferences: (patch: Partial<TravelPreferences>) => void;
    togglePreferenceItem: (
        key: 'preferredAirlines' | 'avoidedAirlines' | 'hotelAmenities' | 'propertyTypes' | 'favoriteDestinations' | 'interests' | 'dietaryNeeds',
        value: string,
    ) => void;
    updateNotifications: (patch: Partial<NotificationSettings>) => void;
    addBooking: (booking: Omit<Booking, 'id' | 'userId'>) => void;
    removeBooking: (id: string) => void;
    saveTrip: (trip: Omit<SavedTrip, 'id' | 'savedAt' | 'userId'>) => void;
    removeSavedTrip: (id: string) => void;
    isTripSaved: (destination: string) => boolean;
    logActivity: (text: string, kind?: ActivityEntry['kind']) => void;
    resetProfile: () => void;
};

const ProfileContext = createContext<ProfileContextValue | null>(null);

export const ProfileProvider = ({ children }: { children: React.ReactNode }) => {
    const { user, isAuthenticated } = useAuth0();
    const [profile, setProfile] = useState<ProfileState>(() => {
        if (typeof window === 'undefined') return EMPTY_PROFILE;
        return hydrate(window.localStorage.getItem(STORAGE_KEY));
    });

    // Auth0 resolves asynchronously after mount, so the initial hydrate()
    // above always seeds a guest id first. Once a real login lands, the
    // profile's identity switches to Auth0's stable subject claim instead
    // of the per-browser generated one — bookings/saved trips created from
    // this point carry that real id (see addBooking/saveTrip below).
    // Bookings made earlier as a guest keep whatever id they were stamped
    // with; this does not rewrite history.
    useEffect(() => {
        if (!isAuthenticated || !user?.sub) return;
        setProfile(prev => (prev.userId === user.sub ? prev : { ...prev, userId: user.sub! }));
    }, [isAuthenticated, user?.sub]);

    // Skip the very first write so mounting doesn't immediately re-persist
    // the value we just read.
    const hydrated = useRef(false);
    useEffect(() => {
        if (!hydrated.current) {
            hydrated.current = true;
            return;
        }
        try {
            window.localStorage.setItem(STORAGE_KEY, JSON.stringify(profile));
        } catch {
            // Quota exceeded or storage disabled — preferences stay in memory
            // for this session rather than breaking the page.
        }
    }, [profile]);

    // Keep multiple tabs consistent.
    useEffect(() => {
        const onStorage = (e: StorageEvent) => {
            if (e.key !== STORAGE_KEY) return;
            setProfile(hydrate(e.newValue));
        };
        window.addEventListener('storage', onStorage);
        return () => window.removeEventListener('storage', onStorage);
    }, []);

    const logActivity = useCallback((text: string, kind: ActivityEntry['kind'] = 'account') => {
        setProfile(prev => ({
            ...prev,
            activity: [{ id: newId('act'), text, at: new Date().toISOString(), kind }, ...prev.activity].slice(0, MAX_ACTIVITY),
        }));
    }, []);

    const updateProfile = useCallback<ProfileContextValue['updateProfile']>(patch => {
        setProfile(prev => ({ ...prev, ...patch }));
    }, []);

    const updatePreferences = useCallback<ProfileContextValue['updatePreferences']>(patch => {
        setProfile(prev => ({ ...prev, preferences: { ...prev.preferences, ...patch } }));
    }, []);

    const togglePreferenceItem = useCallback<ProfileContextValue['togglePreferenceItem']>((key, value) => {
        setProfile(prev => {
            const current = prev.preferences[key];
            const next = current.includes(value)
                ? current.filter(v => v !== value)
                : [...current, value];
            return { ...prev, preferences: { ...prev.preferences, [key]: next } };
        });
    }, []);

    const updateNotifications = useCallback<ProfileContextValue['updateNotifications']>(patch => {
        setProfile(prev => ({ ...prev, notifications: { ...prev.notifications, ...patch } }));
    }, []);

    const addBooking = useCallback<ProfileContextValue['addBooking']>(booking => {
        setProfile(prev => {
            const entry: Booking = { ...booking, id: newId('bk'), userId: prev.userId };
            return {
                ...prev,
                bookings: [entry, ...prev.bookings],
                activity: [
                    { id: newId('act'), text: `Booked ${entry.title}`, at: new Date().toISOString(), kind: 'booking' as const },
                    ...prev.activity,
                ].slice(0, MAX_ACTIVITY),
            };
        });
    }, []);

    const removeBooking = useCallback<ProfileContextValue['removeBooking']>(id => {
        setProfile(prev => ({ ...prev, bookings: prev.bookings.filter(b => b.id !== id) }));
    }, []);

    const saveTrip = useCallback<ProfileContextValue['saveTrip']>(trip => {
        setProfile(prev => {
            if (prev.savedTrips.some(t => t.destination.toLowerCase() === trip.destination.toLowerCase())) {
                return prev;
            }
            const entry: SavedTrip = { ...trip, id: newId('trip'), userId: prev.userId, savedAt: new Date().toISOString() };
            return {
                ...prev,
                savedTrips: [entry, ...prev.savedTrips],
                activity: [
                    { id: newId('act'), text: `Saved ${entry.destination}`, at: new Date().toISOString(), kind: 'save' as const },
                    ...prev.activity,
                ].slice(0, MAX_ACTIVITY),
            };
        });
    }, []);

    const removeSavedTrip = useCallback<ProfileContextValue['removeSavedTrip']>(id => {
        setProfile(prev => ({ ...prev, savedTrips: prev.savedTrips.filter(t => t.id !== id) }));
    }, []);

    const isTripSaved = useCallback(
        (destination: string) =>
            profile.savedTrips.some(t => t.destination.toLowerCase() === destination.toLowerCase()),
        [profile.savedTrips],
    );

    const resetProfile = useCallback(() => {
        // A fresh userId too — resetting starts a new identity, not just
        // empty data under the old one.
        setProfile({ ...EMPTY_PROFILE, userId: newId('usr') });
        try {
            window.localStorage.removeItem(STORAGE_KEY);
        } catch {
            /* storage unavailable */
        }
    }, []);

    const value = useMemo<ProfileContextValue>(() => ({
        profile,
        updateProfile,
        updatePreferences,
        togglePreferenceItem,
        updateNotifications,
        addBooking,
        removeBooking,
        saveTrip,
        removeSavedTrip,
        isTripSaved,
        logActivity,
        resetProfile,
    }), [
        profile, updateProfile, updatePreferences, togglePreferenceItem, updateNotifications,
        addBooking, removeBooking, saveTrip, removeSavedTrip, isTripSaved, logActivity, resetProfile,
    ]);

    return <ProfileContext.Provider value={value}>{children}</ProfileContext.Provider>;
};

export const useProfile = (): ProfileContextValue => {
    const ctx = useContext(ProfileContext);
    if (!ctx) throw new Error('useProfile must be used inside a ProfileProvider');
    return ctx;
};
